<template>
  <div class="form-viewer">
    <h2 v-if="title" class="form-title">{{ title }}</h2>
    <form @submit.prevent="handleSubmit" ref="formEl">
      <div 
        v-for="(field, idx) in parsedFields" 
        :key="idx" 
        class="field-wrapper"
        v-show="shouldShowField(field)"
        :class="{ 'field-disabled': isFieldDisabled(field) }"
      >
        <!-- HEADER FIELD -->
        <div v-if="field.type === 'header'" class="form-header">
          <h3 v-if="field.label" class="header-text">{{ cleanLabel(field.label) }}</h3>
          <h3 v-else-if="field.value" class="header-text">{{ cleanLabel(field.value) }}</h3>
        </div>

        <!-- DIVIDER FIELD -->
        <v-divider v-else-if="field.type === 'divider'" class="form-divider my-4" />

        <!-- LABEL (only show for data fields) -->
        <label v-else-if="field.type !== 'header' && field.type !== 'divider'" :for="field.name" class="field-label">
          {{ cleanLabel(field.label) }}
          <span v-if="field.required" class="required-mark">*</span>
        </label>

        <!-- TEXT INPUT -->
        <input
          v-if="field.type === 'text'"
          :id="field.name"
          :name="field.name"
          :type="field.subtype || 'text'"
          :class="field.className"
          :required="field.required && !readOnly"
          :maxlength="field.maxlength"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          class="field-input"
          :value="field.value"
          @input="onInputChange(field, $event)"
        />

        <!-- NUMBER INPUT -->
        <v-text-field
          v-else-if="field.type === 'number'"
          :id="field.name"
          :name="field.name"
          type="number"
          inputmode="decimal"
          :min="field.min"
          :max="field.max"
          :step="field.step ?? 'any'"
          :required="field.required && !readOnly"
          :model-value="field.value"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          :hint="field.help"
          :persistent-hint="!!field.help"
          @update:model-value="(val) => onNumberChange(field, val)"
          variant="outlined"
          density="compact"
          hide-details="auto"
        />

        <!-- EMAIL INPUT -->
        <v-text-field
          v-else-if="field.type === 'email'"
          :id="field.name"
          :name="field.name"
          type="email"
          :required="field.required && !readOnly"
          :maxlength="field.maxlength"
          :model-value="field.value"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          @update:model-value="(val) => onInputChange(field, val)"
          variant="outlined"
          density="compact"
          hide-details="auto"
        />

        <!-- PHONE NUMBER INPUT -->
        <v-text-field
          v-else-if="field.type === 'phone'"
          :id="field.name"
          :name="field.name"
          type="tel"
          :required="field.required && !readOnly"
          :maxlength="field.maxlength"
          :model-value="field.value"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          @update:model-value="(val) => onInputChange(field, val)"
          variant="outlined"
          density="compact"
          hide-details="auto"
        />

        <!-- DATE INPUT -->
        <v-text-field
          v-else-if="field.type === 'date'"
          :id="field.name"
          :name="field.name"
          type="date"
          :required="field.required && !readOnly"
          :model-value="field.value"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          @update:model-value="(val) => onInputChange(field, val)"
          variant="outlined"
          density="compact"
          hide-details="auto"
        />

        <!-- DATE-TIME INPUT -->
        <v-text-field
          v-else-if="field.type === 'datetime'"
          :id="field.name"
          :name="field.name"
          type="datetime-local"
          :required="field.required && !readOnly"
          :model-value="field.value"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          @update:model-value="(val) => onInputChange(field, val)"
          variant="outlined"
          density="compact"
          hide-details="auto"
        />

        <!-- SIGNATURE FIELD -->
        <div v-else-if="field.type === 'signature'" class="signature-wrapper">
          <div 
            v-if="!readOnly"
            class="signature-canvas-container"
            :style="{ width: (field.width || 400) + 'px', height: (field.height || 200) + 'px' }"
          >
            <canvas
              :ref="el => setSignatureRef(field.name, el)"
              :id="`signature-${field.name}`"
              class="signature-canvas"
              :width="field.width || 400"
              :height="field.height || 200"
            ></canvas>
          </div>
          <div v-else-if="field.value" class="signature-display">
            <img :src="field.value" alt="Signature" style="max-width: 100%; border: 1px solid #ccc; border-radius: 4px;" />
          </div>
          <div v-else class="text-medium-emphasis">No signature</div>
          <div v-if="!readOnly" class="signature-actions mt-2">
            <v-btn size="small" variant="outlined" @click="clearSignature(field)">
              <v-icon start size="16">mdi-eraser</v-icon>
              Clear
            </v-btn>
          </div>
        </div>

        <!-- RATING FIELD -->
        <div v-else-if="field.type === 'rating'" class="rating-wrapper">
          <div v-if="!readOnly" class="rating-input">
            <v-rating
              :id="field.name"
              :name="field.name"
              :model-value="field.value || 0"
              :length="field.maxRating || 5"
              :readonly="readOnly"
              :disabled="readOnly"
              :required="field.required && !readOnly"
              color="warning"
              size="large"
              @update:model-value="(val) => onInputChange(field, val)"
            >
              <template #item="{ props, index }">
                <v-icon
                  v-bind="props"
                  :color="props.modelValue > index ? 'warning' : 'grey-lighten-1'"
                  size="32"
                >
                  {{ field.ratingType === 'hearts' ? 'mdi-heart' : 'mdi-star' }}
                </v-icon>
              </template>
            </v-rating>
            <div v-if="field.ratingLabel && field.value" class="rating-label mt-2 text-body-2">
              {{ getRatingLabel(field, field.value) }}
            </div>
          </div>
          <div v-else class="rating-display d-flex align-center">
            <v-rating
              :model-value="field.value || 0"
              :length="field.maxRating || 5"
              readonly
              color="warning"
              size="large"
              density="compact"
            />
            <span v-if="field.ratingLabel && field.value" class="ml-2 text-body-2">
              {{ getRatingLabel(field, field.value) }}
            </span>
          </div>
        </div>

        <!-- LOOKUP FIELD -->
        <div v-else-if="field.type === 'lookup'" class="lookup-wrapper">
          <v-autocomplete
            :id="field.name"
            :name="field.name"
            :model-value="field.value"
            :items="lookupItems[field.lookupType] || []"
            :item-title="field.lookupDisplayField || 'name'"
            :item-value="getLookupValueField(field)"
            :multiple="field.lookupAllowMultiple"
            :required="field.required && !readOnly"
            :readonly="readOnly"
            :disabled="readOnly"
            :placeholder="field.lookupSearchPlaceholder || 'Search...'"
            :loading="lookupLoading[field.lookupType]"
            variant="outlined"
            density="compact"
            hide-details="auto"
            @update:model-value="(val) => onInputChange(field, val)"
            @focus="loadLookupData(field)"
          />
        </div>

        <!-- BARCODE FIELD -->
        <div v-else-if="field.type === 'barcode'" class="barcode-wrapper">
          <div class="d-flex align-center ga-2">
            <v-text-field
              :id="field.name"
              :name="field.name"
              :model-value="field.value"
              :required="field.required && !readOnly"
              :readonly="readOnly"
              :disabled="readOnly"
              placeholder="Scan or enter barcode"
              variant="outlined"
              density="compact"
              hide-details="auto"
              @update:model-value="(val) => onInputChange(field, val)"
            >
              <template #append-inner>
                <v-btn
                  v-if="!readOnly"
                  icon
                  size="small"
                  variant="text"
                  @click="startBarcodeScanner(field)"
                >
                  <v-icon>mdi-barcode-scan</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>
          <v-dialog v-model="barcodeScannerDialog" max-width="500" @update:model-value="(val) => { if (!val) stopBarcodeScanner(); }">
            <v-card>
              <v-card-title>Scan Barcode</v-card-title>
              <v-card-text>
                <div :id="`barcode-scanner-${field.name}`" style="width: 100%; min-height: 300px;"></div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn @click="barcodeScannerDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>

        <!-- TEXTAREA -->
        <textarea
          v-else-if="field.type === 'textarea'"
          :id="field.name"
          :name="field.name"
          :rows="field.rows || 3"
          :class="field.className"
          :required="field.required && !readOnly"
          :maxlength="field.maxlength"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          class="field-textarea"
          :value="field.value"
          @input="onInputChange(field, $event)"
        ></textarea>

        <!-- SELECT -->
        <v-select
          v-else-if="field.type === 'select'"
          :id="field.name"
          :name="field.name"
          :items="field.values || []"
          item-title="label"
          item-value="value"
          :required="field.required && !readOnly"
          :multiple="field.multiple"
          :model-value="getSelectValue(field)"
          :readonly="readOnly || isFieldDisabled(field)"
          :disabled="readOnly || isFieldDisabled(field)"
          :hint="field.help"
          :persistent-hint="!!field.help"
          clearable
          variant="outlined"
          density="compact"
          hide-details="auto"
          @update:model-value="(val) => onSelectValueChange(field, val)"
        />

        <!-- RADIO GROUP -->
        <div v-else-if="field.type === 'radio-group'" class="field-radio-group">
          <div v-for="(opt, oidx) in field.values" :key="oidx" class="radio-option">
            <input
              type="radio"
              :id="`${field.name}-${oidx}`"
              :name="field.name"
              :value="opt.value"
              :checked="opt.selected"
              :required="field.required && !readOnly"
              :disabled="readOnly"
              class="field-radio"
              @change="onRadioChange(field, opt.value)"
            />
            <label :for="`${field.name}-${oidx}`">{{ opt.label }}</label>
          </div>
        </div>

        <!-- CHECKBOX GROUP -->
        <div v-else-if="field.type === 'checkbox-group'" class="field-checkbox-group">
          <div v-for="(opt, oidx) in field.values" :key="oidx" class="checkbox-option">
            <input
              type="checkbox"
              :id="`${field.name}-${oidx}`"
              :name="field.name"
              :value="opt.value"
              :checked="opt.selected"
              :required="field.required && !field.values.some((o) => o.selected) && !readOnly"
              :disabled="readOnly"
              class="field-checkbox"
              @change="onCheckboxChange(field, $event, opt.value)"
            />
            <label :for="`${field.name}-${oidx}`">{{ opt.label }}</label>
          </div>
        </div>

        <!-- FILE UPLOAD -->
        <input
          v-else-if="field.type === 'file'"
          type="file"
          :id="field.name"
          :name="field.name"
          :class="field.className"
          @change="handleFileChange($event, field)"
          :required="field.required && !readOnly"
          :disabled="readOnly"
          class="field-file"
        />

        <!-- AUDIO RECORDING FIELD (Feature #23) -->
        <div v-else-if="(field.type || '').toLowerCase() === 'audio'" class="audio-field-wrapper">
          <AudioRecorder
            :model-value="field.value"
            :max-duration="field.maxDuration || 300"
            @update:model-value="(val) => updateFieldValue(field, val)"
          />
        </div>

        <!-- FALLBACK FOR UNKNOWN TYPE (but skip header/divider as they're handled above) -->
        <p v-else-if="field.type !== 'header' && field.type !== 'divider'" class="unsupported-field">
          Unsupported field type: <strong>{{ field.type }}</strong>
        </p>
      </div>

      <!-- SUBMIT BUTTON -->
      <v-btn v-if="showSubmit" type="submit" class="submit-btn" color="success" dark small flat>
        <v-icon left size="18">mdi-content-save</v-icon> {{ submitLabel }}
      </v-btn>
    </form>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import pmApi from '@/api/pm';
import { fetchZones } from '@/api/zones';
import { uploadAudio } from '@/api/audio';
import AudioRecorder from './AudioRecorder.vue';

export default {
  name: "FormViewer",
  components: {
    AudioRecorder,
  },
  props: {
    formJson: {
      type: [String, Array],
      default: "",
    },
    fields: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: "Dynamic Form",
    },
    submitLabel: {
      type: String,
      default: "Submit",
    },
    showSubmit: { type: Boolean, default: true },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      parsedFields: [],
      submittingInternal: false,
      // Signature pad instances
      signaturePads: {},
      // Lookup data cache
      lookupItems: {},
      lookupLoading: {},
      // Barcode scanner
      barcodeScannerDialog: false,
      currentBarcodeField: null,
      barcodeScanner: null,
      
      // Conditional logic state
      fieldVisibility: {}, // Cache for field visibility
      fieldDisabledState: {}, // Cache for disabled state
    };
  },
  watch: {
    formJson: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.parsedFields = [];
          return;
        }
        if (typeof val === "string") {
          try {
            this.parsedFields = JSON.parse(val);
          } catch (err) {
            this.parsedFields = [];
          }
        } else if (Array.isArray(val)) {
          this.parsedFields = val;
        } else if (val && typeof val === 'object' && Array.isArray(val.fields)) {
          this.parsedFields = val.fields;
        } else {
          this.parsedFields = [];
        }
      },
    },
    fields: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && val.length > 0) {
          this.parsedFields = val;
        }
      },
    },
    parsedFields: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.parsedFields.forEach((field) => {
            if (field.type === 'signature' && !this.readOnly) {
              this.initSignaturePad(field);
            }
          });
          // Re-evaluate all conditional logic when fields change
          this.evaluateAllConditionalLogic();
        });
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.parsedFields.forEach((field) => {
        if (field.type === 'signature' && !this.readOnly) {
          this.initSignaturePad(field);
        }
      });
      // Initial evaluation of conditional logic
      this.evaluateAllConditionalLogic();
    });
  },
  beforeUnmount() {
    // Clean up signature pads
    Object.values(this.signaturePads).forEach(pad => {
      if (pad) pad.clear();
    });
    // Clean up barcode scanner
    this.stopBarcodeScanner();
  },
  methods: {
    cleanLabel(value) {
      if (value == null) return "";
      return String(value)
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&lt;/gi, "<")
        .replace(/&gt;/gi, ">")
        .replace(/\s+/g, " ")
        .trim();
    },
    updateFieldValue(field, newValue) {
      if (["text", "textarea", "number", "file", "email", "phone", "date", "datetime", "signature", "rating", "lookup", "barcode", "audio"].includes(field.type)) {
        field.value = newValue;
      } else if (field.type === "select") {
        if (field.multiple) {
          field.values.forEach((opt) => {
            opt.selected = newValue.includes(opt.value);
          });
        } else {
          field.values.forEach((opt) => {
            opt.selected = opt.value === newValue;
          });
        }
      } else if (field.type === "radio-group") {
        field.values.forEach((opt) => {
          opt.selected = opt.value === newValue;
        });
      } else if (field.type === "checkbox-group") {
        field.values.forEach((opt) => {
          opt.selected = newValue.includes(opt.value);
        });
      }
    },

    onInputChange(field, event) {
      // Handle both native events and Vuetify events
      const value = event?.target?.value ?? event;
      this.updateFieldValue(field, value);
      // Re-evaluate conditional logic when field value changes
      this.$nextTick(() => {
        this.evaluateAllConditionalLogic();
      });
    },

    onNumberChange(field, value) {
      const numberValue = value === "" || value == null ? null : Number(value);
      this.updateFieldValue(field, Number.isFinite(numberValue) ? numberValue : null);
      this.$nextTick(() => this.evaluateAllConditionalLogic());
    },

    onSelectChange(field, event) {
      let value;
      if (field.multiple) {
        const selectedOptions = Array.from(event.target.selectedOptions);
        value = selectedOptions.map((opt) => opt.value);
      } else {
        value = event.target.value;
      }
      this.updateFieldValue(field, value);
      // Re-evaluate conditional logic when field value changes
      this.$nextTick(() => {
        this.evaluateAllConditionalLogic();
      });
    },

    getSelectValue(field) {
      if (field.multiple) {
        if (Array.isArray(field.value)) return field.value;
        return (field.values || []).filter((option) => option.selected).map((option) => option.value);
      }
      if (field.value !== undefined && field.value !== null && field.value !== '') return field.value;
      return (field.values || []).find((option) => option.selected)?.value ?? null;
    },

    onSelectValueChange(field, value) {
      this.updateFieldValue(field, value);
      this.$nextTick(() => this.evaluateAllConditionalLogic());
    },

    onRadioChange(field, value) {
      this.updateFieldValue(field, value);
      // Re-evaluate conditional logic when field value changes
      this.$nextTick(() => {
        this.evaluateAllConditionalLogic();
      });
    },

    onCheckboxChange(field, event, optionValue) {
      let selectedValues = field.values.filter((opt) => opt.selected).map((opt) => opt.value);

      if (event.target.checked) {
        if (!selectedValues.includes(optionValue)) {
          selectedValues.push(optionValue);
        }
      } else {
        selectedValues = selectedValues.filter((v) => v !== optionValue);
      }
      this.updateFieldValue(field, selectedValues);
      // Re-evaluate conditional logic when field value changes
      this.$nextTick(() => {
        this.evaluateAllConditionalLogic();
      });
    },

    handleFileChange(event, field) {
      const file = event.target.files ? event.target.files[0] : null;
      field.value = file;
    },

    // Signature methods
    setSignatureRef(fieldName, el) {
      if (el && !this.signaturePads[fieldName]) {
        this.$nextTick(() => {
          this.initSignaturePad({ name: fieldName, ...this.parsedFields.find(f => f.name === fieldName) });
        });
      }
    },

    initSignaturePad(field) {
      if (!field || !field.name) return;
      
      this.$nextTick(() => {
        const canvas = document.getElementById(`signature-${field.name}`);
        if (!canvas || this.signaturePads[field.name]) return;
        
        const pad = new SignaturePad(canvas, {
          backgroundColor: field.backgroundColor || '#ffffff',
        });
        
        this.signaturePads[field.name] = pad;
        
        pad.addEventListener('endStroke', () => {
          field.value = pad.toDataURL('image/png');
        });
        
        // Load existing signature if any
        if (field.value) {
          const img = new Image();
          img.src = field.value;
          img.onload = () => {
            pad.clear();
            pad.fromDataURL(field.value);
          };
        }
      });
    },

    clearSignature(field) {
      const pad = this.signaturePads[field.name];
      if (pad) {
        pad.clear();
        field.value = null;
      }
    },

    // Rating methods
    getRatingLabel(field, value) {
      if (!field.ratingLabel || !value || value === 0) return '';
      const labels = field.ratingLabel.split(',').map(l => l.trim());
      return labels[value - 1] || '';
    },

    // Lookup methods
    getLookupValueField(field) {
      // Return the ID field name based on entity type
      const map = {
        assets: 'assetId',
        zones: 'zoneId',
        users: 'userId',
        workorders: 'workOrderId',
        inspections: 'inspectionId',
        findings: 'findingId',
        tasks: 'taskId',
      };
      return map[field.lookupType] || 'id';
    },

    async loadLookupData(field) {
      if (!field.lookupType) return;
      if (this.lookupItems[field.lookupType]?.length > 0) return; // Already loaded
      
      this.lookupLoading[field.lookupType] = true;
      try {
        let data = [];
        switch (field.lookupType) {
          case 'assets':
            const assetsRes = await pmApi.getAssets();
            data = assetsRes?.data || [];
            break;
          case 'zones':
            data = await fetchZones();
            break;
          case 'users':
            // Add users API call when available
            data = [];
            break;
          case 'workorders':
            const woRes = await pmApi.getWorkOrders();
            data = woRes?.data || [];
            break;
          case 'inspections':
            // Add inspections API call when available
            data = [];
            break;
          case 'findings':
            // Add findings API call when available
            data = [];
            break;
          case 'tasks':
            // Add tasks API call when available
            data = [];
            break;
        }
        this.lookupItems[field.lookupType] = data;
      } catch (err) {
        this.lookupItems[field.lookupType] = [];
      } finally {
        this.lookupLoading[field.lookupType] = false;
      }
    },

    // Barcode methods
    async startBarcodeScanner(field) {
      this.currentBarcodeField = field;
      this.barcodeScannerDialog = true;

      this.$nextTick(async () => {
        const containerId = `barcode-scanner-${field.name}`;
        const container = document.getElementById(containerId);
        if (!container) return;

        const scanConfig = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
          ],
        };

        const onScanSuccess = (decodedText) => {
          field.value = decodedText;
          this.stopBarcodeScanner();
          this.barcodeScannerDialog = false;
        };

        const onScanError = (errorMessage) => {
          const msg = String(errorMessage || '');
          if (
            !msg ||
            msg.includes('NotFoundException') ||
            msg.includes('No MultiFormat Readers were able to detect the code') ||
            msg.includes('QR code parse error')
          ) {
            return;
          }
          console.warn('Barcode scan error:', errorMessage);
        };

        try {
          this.barcodeScanner = new Html5Qrcode(containerId);

          try {
            await this.barcodeScanner.start(
              { facingMode: { exact: 'environment' } },
              scanConfig,
              onScanSuccess,
              onScanError
            );
          } catch (environmentErr) {
            const cameras = await Html5Qrcode.getCameras();
            const fallbackCameraId = cameras?.[0]?.id;

            if (!fallbackCameraId) {
              throw environmentErr;
            }

            await this.barcodeScanner.start(
              fallbackCameraId,
              scanConfig,
              onScanSuccess,
              onScanError
            );
          }
        } catch (err) {
          console.warn('Failed to start barcode scanner:', err);
          if (field.barcodeAllowManual) {
            this.barcodeScannerDialog = false;
          }
        }
      });
    },

    stopBarcodeScanner() {
      if (this.barcodeScanner) {
        this.barcodeScanner.stop().then(() => {
          this.barcodeScanner.clear();
          this.barcodeScanner = null;
        }).catch((err) => {
          this.barcodeScanner = null;
        });
      }
    },

    // Conditional Logic Evaluation Methods
    evaluateAllConditionalLogic() {
      this.parsedFields.forEach(field => {
        if (field.conditionalLogic) {
          const result = this.evaluateConditionalLogic(field);
          this.fieldVisibility[field.name] = result.visible;
          this.fieldDisabledState[field.name] = result.disabled;
          
          // Handle require action
          if (field.conditionalLogic.action === 'require') {
            field.required = result.visible;
          }
        } else {
          // No conditional logic = always visible and enabled
          this.fieldVisibility[field.name] = true;
          this.fieldDisabledState[field.name] = false;
        }
      });
    },

    evaluateConditionalLogic(field) {
      if (!field.conditionalLogic || !field.conditionalLogic.conditions || field.conditionalLogic.conditions.length === 0) {
        return { visible: true, disabled: false };
      }

      const { action, conditions } = field.conditionalLogic;
      const logicOperator = field.conditionalLogic.logicOperator || field.conditionalLogic.operator || 'AND';
      
      // Evaluate all conditions
      const results = conditions.map(condition => {
        const targetField = this.parsedFields.find(f => f.name === condition.field);
        if (!targetField) return false;
        
        return this.evaluateCondition(condition, targetField);
      });
      
      // Combine results based on AND/OR
      const passed = logicOperator === 'AND' 
        ? results.every(r => r) 
        : results.some(r => r);
      
      // Apply action
      switch(action) {
        case 'show':
          return { visible: passed, disabled: false };
        case 'hide':
          return { visible: !passed, disabled: false };
        case 'enable':
          return { visible: true, disabled: !passed };
        case 'disable':
          return { visible: true, disabled: passed };
        case 'require':
          return { visible: true, disabled: false, require: passed };
        default:
          return { visible: true, disabled: false };
      }
    },

    evaluateCondition(condition, targetField) {
      const { operator, value } = condition;
      let fieldValue = targetField.value;
      
      // Handle different field types
      if (targetField.type === 'select' || targetField.type === 'radio-group') {
        // For select/radio, get the selected value
        if (targetField.values) {
          const selected = targetField.values.find(v => v.selected);
          fieldValue = selected ? selected.value : null;
        }
      } else if (targetField.type === 'checkbox-group') {
        // For checkbox groups, get array of selected values
        fieldValue = targetField.values.filter(v => v.selected).map(v => v.value);
      }
      
      switch(operator) {
        case 'equals':
          return String(fieldValue) === String(value);
        case 'not_equals':
          return String(fieldValue) !== String(value);
        case 'contains':
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => String(v).includes(String(value)));
          }
          return String(fieldValue).includes(String(value));
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => String(v).includes(String(value)));
          }
          return !String(fieldValue).includes(String(value));
        case 'greater_than':
          return Number(fieldValue) > Number(value);
        case 'less_than':
          return Number(fieldValue) < Number(value);
        case 'is_empty':
          if (Array.isArray(fieldValue)) {
            return fieldValue.length === 0;
          }
          return !fieldValue || String(fieldValue).trim() === '';
        case 'is_not_empty':
          if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0;
          }
          return !!fieldValue && String(fieldValue).trim() !== '';
        default:
          return false;
      }
    },

    shouldShowField(field) {
      if (!field.conditionalLogic) return true;
      return this.fieldVisibility[field.name] !== false;
    },

    isFieldDisabled(field) {
      if (this.readOnly) return true;
      if (!field.conditionalLogic) return false;
      return this.fieldDisabledState[field.name] === true;
    },

    // Prepare the form answers for submission: bake signature pads into values
    // and upload any audio Blobs (which can't be JSON-serialized). Returns the
    // serializable field array, or null if an audio upload failed. Exposed so a
    // parent (e.g. a single combined Save button) can persist the form directly
    // without going through the @submit event.
    async preparePayload() {
      this.parsedFields.forEach((field) => {
        if ((field.type || '').toLowerCase() !== 'signature') return;
        const pad = this.signaturePads[field.name];
        if (pad && !pad.isEmpty()) {
          field.value = pad.toDataURL('image/png');
        }
      });

      // Upload audio Blobs before submit (they can't be JSON-serialized)
      const isBlob = (v) => v instanceof Blob || (v && typeof v === 'object' && 'size' in v && 'type' in v);
      const audioFields = this.parsedFields.filter(
        (f) => (f.type || '').toLowerCase() === "audio" && isBlob(f.value) && f.value.size > 0
      );
      for (const field of audioFields) {
        try {
          const url = await uploadAudio(field.value);
          field.value = url;
        } catch (err) {
          console.error("Audio upload failed:", err);
          this.$toast?.error?.(`Failed to upload audio: ${err.message}`);
          return null;
        }
      }
      return JSON.parse(JSON.stringify(this.parsedFields));
    },
    async handleSubmit() {
      if (this.submittingInternal) return;
      this.submittingInternal = true;

      try {
        const payload = await this.preparePayload();
        if (payload) this.$emit("submit", payload);
      } finally {
        setTimeout(() => {
          this.submittingInternal = false;
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.form-viewer {
  max-width: 680px;
  margin: 0 auto;
  font-family: var(--font-family-app);
}

.form-title {
  text-align: center;
  margin-bottom: 1rem;
  font-size: 1.75rem;
  line-height: 1.15;
  font-weight: 800;
  color: #1f2937;
  letter-spacing: -0.02em;
}

.field-wrapper {
  margin-bottom: 0.7rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid #e7edf3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
}

.field-label {
  display: block;
  margin-bottom: 0.3rem;
  font-weight: 700;
  font-size: 0.9rem;
  color: #344054;
  line-height: 1.25;
}

.required-mark {
  color: #c00;
  margin-left: 0.2rem;
}

.field-input,
.field-textarea,
.field-select,
.field-file {
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  box-sizing: border-box;
  font-size: 0.96rem;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-input:focus,
.field-textarea:focus,
.field-select:focus,
.field-file:focus {
  outline: none;
  border-color: #137752;
  box-shadow: 0 0 0 4px rgba(19, 119, 82, 0.12);
}

.field-textarea {
  resize: vertical;
  min-height: 84px;
}

.field-radio-group,
.field-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.radio-option,
.checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0;
  padding: 0.55rem 0.7rem;
  border: 1px solid #e7edf3;
  border-radius: 10px;
  background: #fff;
}

.unsupported-field {
  color: #c00;
  font-size: 0.9rem;
}

.form-header {
  margin: 0.2rem 0 0.1rem 0;
  padding: 0.2rem 0 0.55rem 0;
  border-bottom: 2px solid #e7edf3;
}

.form-header .header-text {
  font-size: 1.05rem;
  font-weight: 800;
  color: #137752;
  margin: 0;
  padding: 0;
}

.form-divider {
  margin: 1rem 0;
}

.submit-btn {
  min-width: 160px;
  margin-top: 0.5rem;
  background: #137752;
  color: white;
  border: none;
  padding: 0.75rem 1.35rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(19, 119, 82, 0.18);
}
.submit-btn:hover {
  background: #106443;
}

/* Signature field styles */
.signature-wrapper {
  margin-bottom: 0.4rem;
}

.signature-canvas-container {
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #fff;
  margin-bottom: 0.5rem;
}

.signature-canvas {
  display: block;
  cursor: crosshair;
  touch-action: none;
}

.signature-display {
  margin-bottom: 0.5rem;
}

.signature-actions {
  display: flex;
  gap: 0.5rem;
}

/* Rating field styles */
.rating-wrapper {
  margin-bottom: 1rem;
}

.rating-label {
  font-weight: 500;
  color: #666;
}

/* Lookup field styles */
.lookup-wrapper {
  margin-bottom: 1rem;
}

/* Barcode field styles */
.barcode-wrapper {
  margin-bottom: 1rem;
}

/* Conditional logic styles */
.field-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.form-viewer :deep(.v-field) {
  border-radius: 10px !important;
  box-shadow: none !important;
}

.form-viewer :deep(.v-field--variant-outlined .v-field__outline) {
  color: #d0d5dd !important;
}

.form-viewer :deep(.v-field--focused .v-field__outline) {
  color: #137752 !important;
}

.form-viewer :deep(.v-field__input) {
  min-height: 42px;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.form-viewer :deep(.v-selection-control) {
  margin: 0;
}

.form-viewer :deep(.v-input) {
  margin-bottom: 0 !important;
}

.form-viewer :deep(textarea.v-field__input) {
  min-height: 84px;
}
</style>
